import {
  ComponentProps,
  ForwardRefExoticComponent,
  RefAttributes,
  forwardRef,
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { SearchResult } from '@backstage/plugin-search-common';
import { Link } from '@backstage/core-components';
import { HighlightedSearchResultText } from '@backstage/plugin-search-react';
import { extractResultMetadata } from './searchResultMetadata';
import { resolveLocation } from './resolveResultLocation';

export type QuickSearchResultItemProps = {
  result: SearchResult;
  rank: number; // 1-based; used for aria-posinset
  selected: boolean; // true when this row is keyboard-highlighted
  onActivate: () => void; // called on mouse click; Enter is handled at modal level
};

// Link is typed as a plain function but is a forwardRef component at runtime.
// This cast lets us attach a ref to the underlying <a> element so the parent
// modal can call .click() for keyboard Enter navigation.
const ForwardableLink = Link as ForwardRefExoticComponent<
  ComponentProps<typeof Link> & RefAttributes<HTMLAnchorElement>
>;

export const QuickSearchResultItem = forwardRef<
  HTMLAnchorElement,
  QuickSearchResultItemProps
>(function QuickSearchResultItem({ result, rank, selected, onActivate }, ref) {
  const meta = extractResultMetadata(result);
  const location = resolveLocation(result);

  // Show a content excerpt when the search engine highlighted text in the
  // body rather than (or in addition to) the title.  This surfaces what
  // actually matched for results whose titles don't contain the search term.
  const contentHighlight = result.highlight?.fields?.text;

  return (
    // Box owns all visual state (selection highlight, hover, ARIA).
    // ForwardableLink inside is the actual anchor; the ref on it lets the
    // modal call .click() for keyboard Enter navigation.
    <Box
      role="option"
      aria-selected={selected}
      aria-posinset={rank}
      sx={{
        bgcolor: selected
          ? theme => alpha(theme.palette.primary.main, 0.1)
          : 'transparent',
        outline: selected
          ? theme => `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
          : 'none',
        outlineOffset: '-2px',
        borderRadius: 1,
        '&:hover': {
          bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
        },
      }}
    >
      <ForwardableLink
        ref={ref}
        to={location}
        onClick={onActivate}
        tabIndex={-1}
        noTrack={false}
        style={{
          display: 'block',
          padding: '8px 16px',
          textDecoration: 'none',
        }}
      >
        <Stack direction="column" spacing={0.25}>
          {/* Title line — use highlighted text when the search engine returns
              highlight ranges, plain text otherwise. HighlightedSearchResultText
              requires all three props so it cannot be rendered when highlight is
              absent. */}
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {result.highlight ? (
              <HighlightedSearchResultText
                text={result.highlight.fields.title ?? result.document.title}
                preTag={result.highlight.preTag}
                postTag={result.highlight.postTag}
              />
            ) : (
              <Typography noWrap variant="body2">
                {result.document.title}
              </Typography>
            )}
          </Box>

          {/* Content excerpt — only when the match was in the body, not the
              title.  Keeps the row compact for title-match results. */}
          {contentHighlight && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              <HighlightedSearchResultText
                text={contentHighlight}
                preTag={result.highlight!.preTag}
                postTag={result.highlight!.postTag}
              />
            </Typography>
          )}

          {/* Chip row — each chip is only rendered when the field is present */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip size="small" label={meta.sourceLabel} />
            {meta.kind && <Chip size="small" label={meta.kind} />}
            {meta.lifecycle && <Chip size="small" label={meta.lifecycle} />}
            {meta.owner && (
              <Chip size="small" label={meta.owner} title={meta.owner} />
            )}
            {meta.entityName && (
              <Chip size="small" label={`in ${meta.entityName}`} />
            )}
          </Stack>
        </Stack>
      </ForwardableLink>
    </Box>
  );
});
